import styles from '~/modules/game-engine/drawing/styles';

const RADIUS = 100;
const CIRCUMFERENCE = 2 * Math.PI * RADIUS;

/**
 * The wait made visible. Paired with an action, so it only ever appears next to something a room
 * can press - a break gets a draining bar in the lyric band instead.
 */
export default function CountdownRing({
  size = 13.75,
  progress,
  label,
  sub,
}: {
  /** Diameter in `rem`, so the ring grows with the numerals inside it - see below. */
  size?: number;
  /** How much of the wait is left, 0 to 1. */
  progress: number;
  label: string;
  sub: string;
}) {
  const drawn = Math.max(0, Math.min(1, progress)) * CIRCUMFERENCE;

  return (
    // Sized in `rem` and the svg filled to it, rather than both taking a pixel count. The numerals
    // in the middle are `rem`, so a fixed 220px ring held its 1080p diameter while they doubled on a
    // 4K screen and "SECONDS" ended up sitting under the rim. `viewBox` keeps the drawing's own
    // coordinates, so the geometry below is unchanged.
    <div className="relative flex items-center justify-center" style={{ width: `${size}rem`, height: `${size}rem` }}>
      <svg
        width="100%"
        height="100%"
        viewBox="0 0 220 220"
        aria-hidden="true"
        className="absolute -rotate-90"
        data-test="countdown-ring"
        data-progress={Math.round(progress * 100)}>
        <circle cx="110" cy="110" r={RADIUS} fill="none" stroke="rgba(255,255,255,0.12)" strokeWidth={10} />
        <circle
          cx="110"
          cy="110"
          r={RADIUS}
          fill="none"
          stroke={styles.colors.text.active}
          strokeWidth={10}
          strokeLinecap="round"
          strokeDasharray={CIRCUMFERENCE}
          strokeDashoffset={CIRCUMFERENCE - drawn}
        />
      </svg>
      <div className="flex flex-col items-center gap-0.5">
        <span className="stroke-text text-[4.75rem] leading-none font-bold tabular-nums">{label}</span>
        <span className="text-[1.25rem] font-bold tracking-[.08em] text-white/55 uppercase">{sub}</span>
      </div>
    </div>
  );
}
